import * as ort from 'onnxruntime-web';
import { decodeOutputs } from './postprocess.js';
import cfg from '../../config.json';

// YuNet 2023mar has a fixed 640×640 input baked into the ONNX graph.
// We letterbox every frame to that size, then unproject boxes back.
const MODEL_INPUT_SIZE = 640;

// Gamma-correction LUT (gamma = 0.60).
// Aggressively brightens dark skin tones while barely touching bright areas.
// e.g. pixel 50  →  96  (+92 %)
//      pixel 80  → 136  (+70 %)
//      pixel 128 → 181  (+41 %)
//      pixel 200 → 225  (+13 %)
//      pixel 240 → 247  ( +3 %)
// Computed once at module load — O(1) per pixel at inference time.
const GAMMA_LUT = new Float32Array(256);
for (let i = 0; i < 256; i++) GAMMA_LUT[i] = Math.pow(i / 255, cfg.detection.gamma) * 255;

export class YuNetDetector {
  static async create(modelPath, { useWebGPU = false } = {}) {
    ort.env.wasm.wasmPaths = '/ort/';
    ort.env.wasm.numThreads = typeof SharedArrayBuffer !== 'undefined' ? 4 : 1;

    const providers = [];
    if (useWebGPU && navigator.gpu) providers.push('webgpu');
    providers.push('wasm');

    const session = await ort.InferenceSession.create(modelPath, {
      executionProviders: providers,
      graphOptimizationLevel: 'all',
    });

    return new YuNetDetector(session);
  }

  constructor(session) {
    this._session   = session;
    this._inputName = session.inputNames[0];
    this._sz        = MODEL_INPUT_SIZE;

    // Reuse canvases across frames to avoid per-frame allocation
    this._lbCanvas        = document.createElement('canvas');
    this._lbCanvas.width  = this._sz;
    this._lbCanvas.height = this._sz;
    this._lbCtx           = this._lbCanvas.getContext('2d', { willReadFrequently: true });

    this._srcCanvas = document.createElement('canvas');
    this._srcCtx    = this._srcCanvas.getContext('2d');
  }

  /**
   * Fit the frame into MODEL_INPUT_SIZE × MODEL_INPUT_SIZE with black padding,
   * preserving aspect ratio (letterboxing).
   * Returns the transform params needed to unproject boxes.
   */
  _letterbox(imageData) {
    const { width: W, height: H } = imageData;
    const sz = this._sz;

    const scale   = Math.min(sz / W, sz / H);
    const scaledW = Math.round(W * scale);
    const scaledH = Math.round(H * scale);
    const padLeft = Math.floor((sz - scaledW) / 2);
    const padTop  = Math.floor((sz - scaledH) / 2);

    if (this._srcCanvas.width !== W || this._srcCanvas.height !== H) {
      this._srcCanvas.width  = W;
      this._srcCanvas.height = H;
    }
    this._srcCtx.putImageData(imageData, 0, 0);

    this._lbCtx.fillStyle = '#000';
    this._lbCtx.fillRect(0, 0, sz, sz);
    this._lbCtx.drawImage(this._srcCanvas, padLeft, padTop, scaledW, scaledH);

    return { scale, padLeft, padTop };
  }

  /**
   * Detect faces in a single ImageData frame.
   * @param {ImageData} imageData
   * @param {number}    threshold  0–1
   * @returns {Promise<Array<{x1,y1,x2,y2,score}>>} boxes in original pixel coords
   */
  async detect(imageData, threshold = 0.4) {
    const { width: origW, height: origH } = imageData;
    const sz = this._sz;

    const { scale, padLeft, padTop } = this._letterbox(imageData);
    const { data } = this._lbCtx.getImageData(0, 0, sz, sz);

    // RGB float32 [0, 255] NCHW.
    // Gamma correction via LUT improves detection on dark skin tones.
    const planeSize = sz * sz;
    const float32   = new Float32Array(3 * planeSize);
    for (let i = 0; i < planeSize; i++) {
      const s = i * 4;
      float32[i]                 = GAMMA_LUT[data[s]];     // R → ch 0
      float32[planeSize + i]     = GAMMA_LUT[data[s + 1]]; // G → ch 1
      float32[2 * planeSize + i] = GAMMA_LUT[data[s + 2]]; // B → ch 2
    }

    const tensor  = new ort.Tensor('float32', float32, [1, 3, sz, sz]);
    const outputs = await this._session.run({ [this._inputName]: tensor });
    tensor.dispose();

    // Decode in letterboxed space, then unproject to original resolution
    const boxes = decodeOutputs(outputs, sz, sz, threshold);
    return boxes.map(b => ({
      x1:    Math.max(0,      (b.x1 - padLeft) / scale),
      y1:    Math.max(0,      (b.y1 - padTop)  / scale),
      x2:    Math.min(origW,  (b.x2 - padLeft) / scale),
      y2:    Math.min(origH,  (b.y2 - padTop)  / scale),
      score: b.score,
    }));
  }

  dispose() {
    this._session.release();
  }
}
